import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLocationDot,
  faMoneyBill1,
  faHeart,
  faCalendarAlt,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import React, { useRef, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import Modal from "../components/modal/modal";

import "swiper/css";
import "swiper/css/navigation";

const TopVakancies = () => {
  const { t } = useTranslation();
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const swiperRef = useRef(null);
  const navigate = useNavigate();
  
  const [topVacancies, setTopVacancies] = useState([]);
  const [likedVacancies, setLikedVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalMessage, setModalMessage] = useState(null);

  const user = JSON.parse(localStorage.getItem("user") || "null");
  const userId = user?.id;

  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return t("no_deadline") || "—";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("hy-AM");
    } catch (e) {
      return dateString;
    }
  };

  // Получение данных
  useEffect(() => {
    const fetchTopVacanciesAndLikes = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log("Fetching top vacancies...");
        const response = await fetch("/api/get_top_vacancies.php", {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        // Проверяем Content-Type
        const contentType = response.headers.get("content-type");
        console.log("Content-Type:", contentType);
        
        // Получаем текст ответа для отладки
        const responseText = await response.text();
        console.log("Raw response:", responseText.substring(0, 200));
        
        // Пытаемся распарсить JSON
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error("JSON parse error:", parseError);
          console.error("First 200 chars:", responseText.substring(0, 200));
          
          // Проверяем, не HTML ли это
          if (responseText.includes("<") && responseText.includes(">")) {
            throw new Error("Server returned HTML instead of JSON");
          } else {
            throw new Error("Invalid JSON response");
          }
        }
        
        console.log("Parsed data:", data);
        
        if (data.success) {
          setTopVacancies(data.vacancies || []);
        } else {
          throw new Error(data.message || t("errorserver"));
        }
        
        // Получаем лайки если есть пользователь
        if (userId) {
          try {
            const likesResponse = await fetch(
              `/api/get_liked_vacancies.php?user_id=${userId}`
            );
            if (likesResponse.ok) {
              const likesData = await likesResponse.json();
              setLikedVacancies(likesData.map(v => Number(v.id)));
            }
          } catch (likesErr) {
            console.error("Error fetching likes:", likesErr);
          }
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTopVacanciesAndLikes();
  }, [userId, t]);

  // Обработка лайка
  const handleLike = async (vacancyId) => {
    if (!user) {
      setModalMessage({
        title: t("attention"),
        text: t("pleaseregister"),
        success: true,
        actions: [
          {
            label: t("ok"),
            onClick: () => {
              navigate("/WorkerChoose");
              setModalMessage(null);
            },
          },
        ],
      });
      localStorage.setItem("pending_like", vacancyId);
      return;
    }

    if (user?.role === "company") {
      setModalMessage({
        title: t("attention"),
        text: t("companies_cannot_like"),
        success: false,
        actions: [
          {
            label: t("ok"),
            onClick: () => setModalMessage(null),
          },
        ],
      });
      return;
    }

    const alreadyLiked = likedVacancies.includes(Number(vacancyId));

    try {
      const res = await fetch("/api/toggle_like.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          vacancy_id: vacancyId,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setLikedVacancies((prev) =>
          alreadyLiked
            ? prev.filter((id) => id !== Number(vacancyId))
            : [...prev, Number(vacancyId)]
        );
      }
    } catch (err) {
      console.error("Error toggling like:", err);
      setModalMessage({
        title: t("error"),
        text: t("errorserver"),
        success: false,
        actions: [
          {
            label: t("ok"),
            onClick: () => setModalMessage(null),
          },
        ],
      });
    }
  };

  // Обработка pending like
  useEffect(() => {
    const pending = localStorage.getItem("pending_like");
    if (user && pending) {
      handleLike(Number(pending));
      localStorage.removeItem("pending_like");
    }
  }, [user]);

  // Инициализация Swiper
  useEffect(() => {
    if (swiperRef.current && prevRef.current && nextRef.current && topVacancies.length > 4) {
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.params.navigation.prevEl = prevRef.current;
          swiperRef.current.params.navigation.nextEl = nextRef.current;
          swiperRef.current.navigation.init();
          swiperRef.current.navigation.update();
        }
      }, 100);
    }
  }, [topVacancies]);

  // Компонент карточки
  const VacancyCard = ({ vacancy }) => {
    const isLiked = likedVacancies.includes(Number(vacancy.id));
    
    return (
   <>
  {/* CSS СТИЛИ ДЛЯ 3D ЭФФЕКТА (добавь это где-нибудь в компоненте один раз) */}
  <style>{`
    .ribbon-3d {
      position: absolute;
      top: -5px; /* Немного выше края карточки для эффекта загиба */
      right: -5px; /* Немного правее края карточки */
      z-index: 10;
      width: 100px;
      height: 100px;
      overflow: hidden;
      pointer-events: none;
    }
    .ribbon-3d-inner {
      position: absolute;
      display: block;
      width: 150px;
      padding: 8px 0;
      background-color: #006674;
      box-shadow: 0 3px 6px rgba(0,0,0,0.3);
      color: white;
      font-size: 12px;
      font-weight: bold;
      text-transform: uppercase;
      text-align: center;
      left: -15px;
      top: 19px;
      transform: rotate(45deg);
      letter-spacing: 1px;
      border: 1px solid rgba(255,255,255,0.2); /* Светлая полоска сверху */
    }
    /* Левый "загиб" */
    .ribbon-3d::before {
      content: '';
      position: absolute;
      z-index: -1;
      border: 3px solid #a16207; /* Темный цвет для тени (dark yellow) */
      border-top-color: transparent;
      border-right-color: transparent;
      top: 0;
      right: 83px; /* Позиция зависит от ширины ленты */
    }
    /* Правый "загиб" */
    .ribbon-3d::after {
      content: '';
      position: absolute;
      z-index: -1;
      border: 3px solid #a16207; /* Темный цвет для тени */
      border-top-color: transparent;
      border-left-color: transparent;
      top: 83px; /* Позиция зависит от ширины ленты */
      right: 0;
    }
  `}</style>

  {/* ТВОЙ ОРИГИНАЛЬНЫЙ КОД */}
  {/* Важно: overflow-hidden убран из article, т.к. 3D загибы должны выходить за края */}
  <article className="w-full pt-3 relative flex flex-col gap-3 items-center rounded-lg shadow-2xl shadow-black cursor-pointer bg-white">
    
    {/* --- 3D ЛЕНТОЧКА "TOP" --- */}
    <div className="ribbon-3d">
      <div className="ribbon-3d-inner">ԹՈՓ</div>
    </div>
    {/* ------------------------- */}

    <div className="w-full px-3 flex justify-start items-center gap-4">
      <div className="flex-shrink-0">
        <img
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl border-4 bg-gray-200 border-white object-contain shadow-lg"
          src={vacancy.companyLogo || "/default-company.png"}
          alt={vacancy.companyName}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/default-company.png";
          }}
        />
      </div>
      {/* Добавлен pr-10, чтобы текст точно не попал под 3D загиб */}
      <div className="flex-grow pr-10">
        <h3 className="text-lg font-semibold text-[#006674] mb-1 leading-tight">
          <Link to={`/vacancy/${vacancy.id}`} className="hover:underline">
            {vacancy.workname || t("unknown_position")}
          </Link>
        </h3>
        <p className="text-sm text-gray-600 mb-2">
          <FontAwesomeIcon icon={faBuilding} className="mr-2" />
          {vacancy.companyName || t("unknown_company")}
        </p>
        <div className="space-y-1 text-sm">
          <p className="text-gray-700">
            <FontAwesomeIcon icon={faLocationDot} className="mr-2" />
            {vacancy.location || "Երևան"}
          </p>
          <p className="text-gray-700 font-medium">
            <FontAwesomeIcon icon={faMoneyBill1} className="mr-2" />
            {vacancy.salary || "Խոսակցական"}
          </p>
        </div>
      </div>
    </div>

    <button
      onClick={() => handleLike(vacancy.id)}
      
      className={`absolute right-3 top-24 z-20 rounded-full border-2 border-white shadow-lg transition-all duration-200 w-9 h-9 flex items-center justify-center
        ${isLiked 
          ? "bg-red-500 text-white hover:bg-red-600" 
          : "bg-gray-400 text-white hover:bg-[#006674]"}
        ${user?.role === "company" ? "pointer-events-none opacity-60" : ""}
      `}
    >
      <FontAwesomeIcon icon={faHeart} />
    </button>

    {/* Важно: добавлено overflow-hidden и rounded-b-lg сюда, чтобы футер не вылезал за скругления */}
    <div className="w-full mt-3 p-3 flex justify-between items-center bg-[#006674] text-white overflow-hidden rounded-b-lg">
      <span className="text-sm">
        <FontAwesomeIcon className="mr-2" icon={faCalendarAlt} />
        {formatDate(vacancy.created_at)}
      </span>
      <Link 
        to={`/vacancy/${vacancy.id}`} 
        className="text-sm hover:underline font-medium"
      >
        {t("seemore")}
      </Link>
    </div>
  </article>
</>
    );
  };

  if (loading) {
    return (
      <section className="w-full flex flex-col items-end gap-5 p-5">
        <h1 className="w-full translate-x-5 text-white max-sm:text-xl uppercase font-bold text-start text-4xl">
          {t("topvacansies")}
        </h1>
        <div className="w-full flex justify-center items-center h-64">
          <div className="text-white text-xl">Բեռնվում է...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="w-full flex flex-col items-end gap-5 p-5">
        <h1 className="w-full translate-x-5 text-white max-sm:text-xl uppercase font-bold text-start text-4xl">
          {t("topvacansies")}
        </h1>
        <div className="w-full flex justify-center items-center h-64">
          <div className="text-red-400 text-xl">Սխալ: {error}</div>
        </div>
      </section>
    );
  }

  if (topVacancies.length === 0) {
    return (
      <section className="w-full flex flex-col items-end gap-5 p-5">
        <h1 className="w-full translate-x-5 text-white max-sm:text-xl uppercase font-bold text-start text-4xl">
          {t("topvacansies")}
        </h1>
        <div className="w-full flex justify-center items-center h-64">
          <div className="text-gray-400 text-xl">ԹՈՓ աշխատատեղեր չկան</div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full flex flex-col items-end gap-5 p-5">
      <h1 className="w-full translate-x-5 text-white max-sm:text-xl uppercase font-bold text-start text-4xl">
        {t("topvacansies")}
      </h1>

      {modalMessage && (
        <Modal
          title={modalMessage.title}
          text={modalMessage.text}
          success={modalMessage.success}
          actions={modalMessage.actions}
          onClose={() => setModalMessage(null)}
        />
      )}

      {topVacancies.length <= 4 ? (
        <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {topVacancies.map((vacancy) => (
            <VacancyCard key={vacancy.id} vacancy={vacancy} />
          ))}
        </div>
      ) : (
        <div className="relative w-full">
          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            modules={[Navigation]}
            slidesPerView={4}
            spaceBetween={20}
            loop={false}
            breakpoints={{
              0: { slidesPerView: 1, spaceBetween: 10 },
              640: { slidesPerView: 2, spaceBetween: 15 },
              768: { slidesPerView: 2, spaceBetween: 20 },
              1024: { slidesPerView: 3, spaceBetween: 20 },
              1200: { slidesPerView: 4, spaceBetween: 20 },
            }}
          >
            {topVacancies.map((vacancy) => (
              <SwiperSlide key={vacancy.id}>
                <VacancyCard vacancy={vacancy} />
              </SwiperSlide>
            ))}
          </Swiper>

          {topVacancies.length > 4 && (
            <>
              <div
                ref={prevRef}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 cursor-pointer bg-black/50 hover:bg-black/70 text-white text-3xl w-10 h-10 flex items-center justify-center rounded-full"
              >
                &#10094;
              </div>
              <div
                ref={nextRef}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 cursor-pointer bg-black/50 hover:bg-black/70 text-white text-3xl w-10 h-10 flex items-center justify-center rounded-full"
              >
                &#10095;
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default TopVakancies;